import React, { useEffect, useState } from "react";
import { Badge, Button, Dropdown, List, Typography, Empty, Spin, Tooltip } from "antd";
import { BellOutlined, CheckOutlined, DeleteOutlined } from "@ant-design/icons";
import useNotification from "stores/notification.store";
import { THEME_COLORS } from "constants/ThemeConstant";

const { Text } = Typography;

const NotificationItem = ({ notification, onMarkAsRead, onDelete }) => {
  const getTypeColor = type => {
    switch (type) {
      case "success":
        return THEME_COLORS.green[500];
      case "error":
        return THEME_COLORS.red[500];
      case "warning":
        return THEME_COLORS.orange[500];
      default:
        return THEME_COLORS.blue[500];
    }
  };

  const formatDate = date => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Agora";
    if (diffMins < 60) return `${diffMins}m atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays < 7) return `${diffDays}d atrás`;
    return notifDate.toLocaleDateString("pt-BR");
  };

  return (
    <div
      style={{
        padding: "12px 16px",
        borderBottom: "1px solid #f0f0f0",
        backgroundColor: notification.isRead ? "transparent" : "#f6f9fc",
        cursor: "pointer",
      }}
      onClick={() => !notification.isRead && onMarkAsRead(notification.uuid)}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: getTypeColor(notification.type),
              }}
            />
            <Text strong>{notification.title}</Text>
          </div>
          <div style={{ marginTop: "4px", marginLeft: "16px" }}>
            <Text
              type="secondary"
              style={{ fontSize: "13px" }}
            >
              {notification.message}
            </Text>
          </div>
          <div style={{ marginTop: "4px", marginLeft: "16px" }}>
            <Text
              type="secondary"
              style={{ fontSize: "11px" }}
            >
              {formatDate(notification.createdAt)}
            </Text>
          </div>
        </div>
        <div style={{ display: "flex", gap: "4px", marginLeft: "8px" }}>
          {!notification.isRead && (
            <Tooltip title="Marcar como lida">
              <Button
                type="text"
                size="small"
                icon={<CheckOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsRead(notification.uuid);
                }}
                style={{ color: THEME_COLORS.green[500] }}
              />
            </Tooltip>
          )}
          <Tooltip title="Deletar">
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(notification.uuid);
              }}
              style={{ color: THEME_COLORS.red[500] }}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

const NavNotificationBell = () => {
  const {
    notifications,
    unreadCount,
    loadingNotification,
    getNotifications,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    initEventSource,
    closeEventSource,
  } = useNotification();

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    getNotifications();
    getUnreadCount();
    initEventSource();

    return () => {
      closeEventSource();
    };
  }, []);

  const handleMarkAsRead = uuid => {
    markAsRead(uuid);
  };

  const handleDelete = uuid => {
    deleteNotification(uuid);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const notificationMenu = (
    <div
      style={{
        width: "380px",
        maxHeight: "500px",
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
    >
      <div
        style={{
          padding: "16px",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text strong>Notificações</Text>
        {unreadCount > 0 && (
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            onClick={handleMarkAllAsRead}
          >
            Marcar todas como lidas
          </Button>
        )}
      </div>

      <div style={{ maxHeight: "400px", overflowY: "auto" }}>
        {loadingNotification ? (
          <div style={{ padding: "40px", textAlign: "center" }}>
            <Spin />
          </div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: "40px" }}>
            <Empty
              description="Nenhuma notificação"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        ) : (
          <List
            dataSource={notifications}
            renderItem={item => (
            <NotificationItem
              notification={item}
              onMarkAsRead={handleMarkAsRead}
              onDelete={handleDelete}
            />
            )}
          />
        )}
      </div>
    </div>
  );

  return (
    <Dropdown
      overlay={notificationMenu}
      trigger={["click"]}
      placement="bottomRight"
      visible={visible}
      onVisibleChange={setVisible}
    >
      <Button
        type="text"
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Badge
          count={unreadCount}
          offset={[-5, 5]}
        >
          <BellOutlined
            style={{
              fontSize: "18px",
              color: THEME_COLORS.gray[600],
            }}
          />
        </Badge>
      </Button>
    </Dropdown>
  );
};

export default NavNotificationBell;
